import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaCheckCircle, FaPaperPlane } from 'react-icons/fa';

const QuoteForm = ({ darkBg = false }) => {
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: '', phone: '', shipmentDetails: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError('');
    try {
      const res = await fetch('https://formsubmit.co/ajax/info@arnoldfreightco.com', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          _subject: `New Quote Request from ${formData.firstName} ${formData.lastName}`,
          _template: 'table',
          _captcha: 'false',
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          phone: formData.phone,
          shipmentDetails: formData.shipmentDetails,
        }),
      });
      const data = await res.json();
      if (!res.ok || data.success === 'false') throw new Error(data.message || 'Failed to send');
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 6000);
      setFormData({ firstName: '', lastName: '', email: '', phone: '', shipmentDetails: '' });
    } catch (err) {
      setError('Could not send. Please email info@arnoldfreightco.com directly.');
    } finally {
      setSending(false);
    }
  };

  const inputStyle = darkBg
    ? { background: 'rgba(15,23,36,0.8)', color: '#fff', borderColor: 'rgba(201,168,76,0.2)' }
    : {};

  const labelStyle = {
    display: 'block', fontSize: '13px', fontWeight: '700',
    marginBottom: '8px', color: darkBg ? '#d1d5db' : '#374151',
    letterSpacing: '0.5px', textTransform: 'uppercase',
  };

  return (
    <motion.form
      onSubmit={handleSubmit}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      style={{
        background: darkBg ? 'rgba(36, 50, 71, 0.9)' : '#ffffff',
        padding: 'clamp(22px, 5vw, 36px)',
        borderRadius: '16px',
        boxShadow: darkBg ? '0 20px 60px rgba(0,0,0,0.3)' : '0 20px 60px rgba(0,0,0,0.08)',
        border: darkBg ? '1px solid rgba(201,168,76,0.15)' : '1px solid #e5e7eb',
        backdropFilter: darkBg ? 'blur(20px)' : 'none',
      }}
    >
      <h3 style={{
        fontSize: '24px', fontWeight: '800', marginBottom: '8px', marginTop: 0,
        color: darkBg ? '#ffffff' : '#1a2332',
      }}>
        Request a Quote
      </h3>
      <div className="gold-line" style={{ marginBottom: '28px' }} />

      <AnimatePresence>
        {submitted && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            style={{
              background: 'rgba(201, 168, 76, 0.12)', border: '2px solid rgba(201,168,76,0.3)',
              padding: '16px', borderRadius: '12px', marginBottom: '20px',
              display: 'flex', alignItems: 'center', gap: '12px',
              color: darkBg ? '#d4b96a' : '#b8943a', fontSize: '15px',
            }}
          >
            <FaCheckCircle size={20} /> Thank you! We'll get back to you shortly.
          </motion.div>
        )}
      </AnimatePresence>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
        {[
          { name: 'firstName', label: 'First Name', placeholder: 'John', type: 'text' },
          { name: 'lastName', label: 'Last Name', placeholder: 'Doe', type: 'text' },
        ].map((field) => (
          <motion.div key={field.name} animate={{ scale: focusedField === field.name ? 1.02 : 1 }} transition={{ type: 'spring', stiffness: 300 }}>
            <label style={labelStyle}>{field.label}</label>
            <input
              type={field.type} name={field.name} value={formData[field.name]}
              onChange={handleChange} onFocus={() => setFocusedField(field.name)} onBlur={() => setFocusedField(null)}
              className="form-input" required placeholder={field.placeholder}
              style={inputStyle}
            />
          </motion.div>
        ))}
      </div>

      {[
        { name: 'email', label: 'Email', placeholder: 'john@company.com', type: 'email' },
        { name: 'phone', label: 'Phone', placeholder: '(555) 000-0000', type: 'tel' },
      ].map((field) => (
        <motion.div key={field.name} style={{ marginBottom: '16px' }} animate={{ scale: focusedField === field.name ? 1.02 : 1 }} transition={{ type: 'spring', stiffness: 300 }}>
          <label style={labelStyle}>{field.label}</label>
          <input
            type={field.type} name={field.name} value={formData[field.name]}
            onChange={handleChange} onFocus={() => setFocusedField(field.name)} onBlur={() => setFocusedField(null)}
            className="form-input" placeholder={field.placeholder}
            style={inputStyle}
          />
        </motion.div>
      ))}

      <motion.div style={{ marginBottom: '28px' }} animate={{ scale: focusedField === 'shipmentDetails' ? 1.02 : 1 }} transition={{ type: 'spring', stiffness: 300 }}>
        <label style={labelStyle}>Shipment Details</label>
        <textarea
          name="shipmentDetails" value={formData.shipmentDetails}
          onChange={handleChange} onFocus={() => setFocusedField('shipmentDetails')} onBlur={() => setFocusedField(null)}
          className="form-input" rows="4"
          placeholder="Origin, destination, freight type, timeline..."
          style={{ ...inputStyle, resize: 'vertical' }}
        />
      </motion.div>

      {error && (
        <div style={{
          background: 'rgba(220,38,38,0.1)', border: '1px solid rgba(220,38,38,0.3)',
          padding: '12px', borderRadius: '8px', marginBottom: '16px',
          color: '#dc2626', fontSize: '14px',
        }}>{error}</div>
      )}

      <motion.button
        type="submit"
        className="btn-gold"
        disabled={sending}
        style={{ width: '100%', fontSize: '16px', padding: '16px', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', opacity: sending ? 0.7 : 1, cursor: sending ? 'not-allowed' : 'pointer' }}
        whileHover={{ scale: sending ? 1 : 1.02 }}
        whileTap={{ scale: sending ? 1 : 0.98 }}
      >
        <FaPaperPlane /> {sending ? 'Sending...' : 'Send Message'}
      </motion.button>
    </motion.form>
  );
};

export default QuoteForm;
